import { useState, useRef, useEffect } from 'react';
import { Button, Input } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { useLocation } from 'wouter';
import { useVictoriaChat, ChatMessage } from '../../hooks/useVictoriaChat';
import useDataStore from '../../../../common/src/stores/useDataStore';
import useUserStore from '../../../../common/src/stores/useUserStore';
import useBrand from '../../../../common/src/hooks/useBrand';
import type { BrandAssets } from '../../../../common/src/utils/branding';
import VictoriaChart from './VictoriaChart';

const SUGGESTIONS = [
  {
    icon: 'tabler:chart-line',
    text: 'Grafícame el FOB de Santina por temporada',
  },
  { icon: 'tabler:coin', text: '¿Cuál es mi FOB promedio esta temporada?' },
  { icon: 'tabler:package', text: '¿Cuánto he despachado a la fecha?' },
  { icon: 'tabler:scale', text: '¿Cómo van mis retornos vs la industria?' },
];

// Identidad del asistente según el dominio: «CherryMarket IA» o «FruitMarkets
// IA», con el símbolo de la marca en el encabezado y junto a cada respuesta.
// Marca fijada cuando carga el chat: el avatar se monta después, cuando la
// URL ya pudo perder el ?brand= de prueba.
let brandAtLoad: BrandAssets | null = null;

function VictoriaAvatar({
  className = 'w-5 h-5 mt-0.5',
}: {
  className?: string;
}) {
  const { assets } = useBrand();
  return (
    <img
      src={(brandAtLoad ?? assets).iconLight}
      alt=''
      className={`object-contain shrink-0 ${className}`}
    />
  );
}

function TypingIndicator() {
  return (
    <div className='flex items-start gap-2'>
      <VictoriaAvatar />
      <div className='bg-default-100 dark:bg-default-50 rounded-2xl rounded-bl-sm px-4 py-3'>
        <div className='flex gap-1 items-center'>
          {[0, 1, 2].map((i) => (
            <motion.span
              key={i}
              className='w-1.5 h-1.5 bg-brand-500 rounded-full'
              animate={{ y: [0, -4, 0] }}
              transition={{
                duration: 0.6,
                repeat: Infinity,
                delay: i * 0.15,
                ease: 'easeInOut',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const markdownStyles = `
.victoria-shimmer {
  background: linear-gradient(90deg, #9ca3af 30%, #3f3f46 50%, #9ca3af 70%);
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: victoria-shimmer 1.6s linear infinite;
}
@keyframes victoria-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.victoria-markdown p { margin: 0 0 0.5em; }
.victoria-markdown p:last-child { margin-bottom: 0; }
.victoria-markdown strong { font-weight: 600; }
.victoria-markdown ul, .victoria-markdown ol { margin: 0.25em 0; padding-left: 1.25em; }
.victoria-markdown li { margin: 0.15em 0; }
.victoria-markdown code { background: rgba(0,0,0,0.06); padding: 0.1em 0.3em; border-radius: 4px; font-size: 0.9em; }
.victoria-markdown a { color: var(--brand-700); font-weight: 600; text-decoration: underline; text-underline-offset: 2px; }
.victoria-markdown a:hover { color: var(--brand-900); }
.victoria-markdown h3, .victoria-markdown h4 { font-weight: 600; margin: 0.5em 0 0.25em; }
`;

// Links in VictorIA's answers. Platform paths ("/settlements/details",
// "/settings#tracking") navigate inside the SPA: a plain <a> would reload the
// app and wipe the chat, which lives in memory. Anything else (WhatsApp,
// Claude) opens in a new tab.
function VictoriaLink({
  href,
  children,
}: {
  href?: string;
  children?: React.ReactNode;
}) {
  const [, setLocation] = useLocation();
  if (href?.startsWith('/')) {
    return (
      <a
        href={href}
        onClick={(e) => {
          e.preventDefault();
          setLocation(href);
          // pushState doesn't fire hashchange; pages that pick a tab from the
          // hash (Configuración) listen for it.
          if (href.includes('#')) {
            window.dispatchEvent(new HashChangeEvent('hashchange'));
          }
        }}
      >
        {children}
      </a>
    );
  }
  return (
    <a href={href} target='_blank' rel='noopener noreferrer'>
      {children}
    </a>
  );
}

// The agent's current action, shown with a shimmer while tools run —
// "Consultando FOB por etiqueta..." style, like agent UIs.
function ActivityIndicator({ activity }: { activity: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className='flex items-center gap-2 pl-7'
    >
      <span className='relative flex h-2 w-2 shrink-0'>
        <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75' />
        <span className='relative inline-flex rounded-full h-2 w-2 bg-brand-600' />
      </span>
      <span className='text-[12px] victoria-shimmer'>{activity}</span>
    </motion.div>
  );
}

export default function VictoriaChat() {
  const [isOpen, setIsOpen] = useState(false);
  // En celular el botón va abajo a la derecha; solo el Dashboard tiene barra
  // de pestañas abajo, y ahí sube para no taparla.
  const [location] = useLocation();
  const hasBottomNav = location === '/dashboard';
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { messages, isLoading, activity, sendMessage, clearChat } =
    useVictoriaChat();
  const { selectedSpecies } = useDataStore();
  const { userProfile } = useUserStore();
  const { assets: brand } = useBrand();
  brandAtLoad = brand;
  const assistantName = `${brand.name} IA`;

  // Species names carry their emoji ("🍒 Cherry", "🥝 Kiwi") — greet with
  // the selected one so VictorIA matches what the user is looking at.
  const speciesEmoji = (() => {
    const first = selectedSpecies?.name?.trim().split(' ')[0] ?? '';
    return first && !/[a-zA-Z0-9]/.test(first) ? first : '🍒';
  })();

  const hasMessages = messages.length > 0;

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen, messages]);

  const handleSend = (text?: string) => {
    const msg = (text || input).trim();
    if (!msg || isLoading) return;
    setInput('');
    sendMessage(msg);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const lastMsg = messages[messages.length - 1];
  const showDots =
    isLoading &&
    !activity &&
    (!lastMsg ||
      lastMsg.role === 'user' ||
      (lastMsg.content === '' && !lastMsg.steps?.length));

  return (
    <>
      <style>{markdownStyles}</style>
      {/* Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            // z-40: los modales y paneles (z-50) quedan por encima del chat.
            className={`fixed ${hasBottomNav ? 'bottom-28' : 'bottom-4'} right-4 z-40 w-[400px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-10rem)] rounded-2xl shadow-2xl flex flex-col overflow-hidden md:bottom-20 md:max-h-[calc(100vh-8rem)] bg-background border border-default-200`}
          >
            {/* Header */}
            <div className='flex items-center gap-2 px-4 py-3 border-b border-default-100 shrink-0'>
              <VictoriaAvatar className='w-5 h-5' />
              <span className='flex-1 text-sm font-semibold text-foreground'>
                {assistantName}
              </span>
              {hasMessages && (
                <Button
                  isIconOnly
                  size='sm'
                  variant='light'
                  aria-label='Nueva conversación'
                  className='text-default-400 min-w-7 h-7'
                  onPress={clearChat}
                >
                  <Icon icon='tabler:edit' width={16} />
                </Button>
              )}
              <Button
                isIconOnly
                size='sm'
                variant='light'
                aria-label='Cerrar'
                className='text-default-400 min-w-7 h-7'
                onPress={() => setIsOpen(false)}
              >
                <Icon icon='tabler:x' width={16} />
              </Button>
            </div>

            {/* Content area */}
            <div className='flex-1 overflow-y-auto px-4 pt-4 pb-2 flex flex-col'>
              {!hasMessages ? (
                /* Empty state */
                <div className='flex-1 flex flex-col justify-center px-2'>
                  <h2 className='text-xl font-bold text-foreground'>
                    Hola
                    {userProfile?.first_name
                      ? `, ${userProfile.first_name}`
                      : ''}{' '}
                    {speciesEmoji}
                  </h2>
                  <p className='text-xl font-bold text-foreground'>
                    ¿En qué te ayudo hoy?
                  </p>
                  <p className='text-[13px] text-default-500 leading-relaxed mt-2'>
                    Consulto tus datos de liquidaciones, despachos y mercado, y
                    te ayudo a moverte por la plataforma.
                  </p>
                  <div className='mt-6 space-y-0.5'>
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s.text}
                        onClick={() => handleSend(s.text)}
                        className='w-full flex items-center gap-3 py-2 text-left group'
                      >
                        <Icon
                          icon={s.icon}
                          width={17}
                          className='text-default-500 shrink-0 group-hover:text-brand-600 transition-colors'
                        />
                        <span className='text-[13px] font-semibold text-foreground group-hover:text-brand-700 transition-colors'>
                          {s.text}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                /* Messages */
                <div className='space-y-3'>
                  {messages
                    .filter(
                      (m) =>
                        m.role === 'user' ||
                        m.content ||
                        m.steps?.length ||
                        m.charts?.length
                    )
                    .map((msg) => {
                      // The step currently running is shown by the activity
                      // indicator below, not duplicated in the trail.
                      const steps = msg.steps ?? [];
                      const doneSteps =
                        msg.id === lastMsg?.id && activity
                          ? steps.slice(0, -1)
                          : steps;
                      return (
                        <motion.div
                          key={msg.id}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2 }}
                          className={`flex ${msg.role === 'user' ? 'justify-end' : 'items-start gap-2'}`}
                        >
                          {msg.role === 'assistant' && <VictoriaAvatar />}
                          <div
                            className={`${
                              msg.charts?.length ? 'w-full' : 'max-w-[80%]'
                            } rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
                              msg.role === 'user'
                                ? 'bg-brand-700 text-white rounded-br-sm whitespace-pre-wrap'
                                : 'bg-content1 text-foreground rounded-bl-sm border border-default-100 victoria-markdown'
                            }`}
                          >
                            {msg.role === 'assistant' ? (
                              <>
                                {doneSteps.length > 0 && (
                                  <div
                                    className={`space-y-1 ${
                                      msg.content
                                        ? 'mb-2 pb-2 border-b border-default-100'
                                        : ''
                                    }`}
                                  >
                                    {doneSteps.map((step, i) => (
                                      <div
                                        key={i}
                                        className='flex items-start gap-1.5 text-[11px] text-default-400 leading-snug'
                                      >
                                        <Icon
                                          icon='tabler:circle-check'
                                          width={12}
                                          className='mt-[1px] shrink-0 text-success-400'
                                        />
                                        <span>{step}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <ReactMarkdown components={{ a: VictoriaLink }}>
                                  {msg.content}
                                </ReactMarkdown>
                                {msg.charts?.map((spec, i) => (
                                  <VictoriaChart key={i} spec={spec} />
                                ))}
                              </>
                            ) : (
                              msg.content
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  {activity && <ActivityIndicator activity={activity} />}
                  {showDots && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <TypingIndicator />
                    </motion.div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Input card */}
            <div className='px-4 pb-3 pt-1 shrink-0'>
              <div className='bg-content1 border border-default-200 rounded-2xl px-3 py-1.5 shadow-sm'>
                <div className='flex items-center gap-2'>
                  <input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder='Pregúntale algo a la IA...'
                    disabled={isLoading}
                    className='flex-1 text-[13px] bg-transparent outline-none placeholder:text-default-300 text-foreground py-1.5'
                  />
                  <button
                    onClick={() => handleSend()}
                    disabled={!input.trim() || isLoading}
                    className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all ${
                      input.trim() && !isLoading
                        ? 'bg-brand-700 text-white hover:bg-brand-800'
                        : 'bg-default-100 text-default-300'
                    }`}
                  >
                    <Icon icon='tabler:arrow-up' width={16} />
                  </button>
                </div>
              </div>
              <p className='text-[10px] text-default-300 text-center mt-2'>
                {assistantName} puede cometer errores.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Button. z-40 para quedar bajo los modales y paneles (z-50);
          en celular solo el ícono, y con el chat abierto se oculta (el panel
          tiene su propio cerrar y el botón le tapaba el pie). */}
      <motion.div
        className={`fixed right-4 z-40 md:bottom-4 ${
          hasBottomNav ? 'bottom-24' : 'bottom-[calc(env(safe-area-inset-bottom)+1rem)]'
        } ${isOpen ? 'hidden md:block' : ''}`}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <Button
          onPress={() => setIsOpen(!isOpen)}
          aria-label='Pregúntale a la IA'
          className='bg-content1 data-[hover=true]:bg-default-100 text-brand-700 border border-default-200 h-12 min-w-12 px-0 md:px-5 rounded-full shadow-lg shadow-black/10'
          startContent={
            <Icon icon={isOpen ? 'tabler:x' : 'tabler:sparkles'} width={18} />
          }
        >
          <span className='hidden font-semibold text-sm md:inline'>Pregúntale a la IA</span>
        </Button>
      </motion.div>
    </>
  );
}
